import { useState, useEffect, useCallback } from 'react';
import { useT } from '../../hooks/useTranslation';
import { useLanguage } from '../../context/LanguageContext';
import { PRE_LAUNCH_MODE } from '../../config/maintenance';
import { seasonContent } from '../../config/season';
import { heroSrcSet, imageSrc } from '../../utils/images';
import { useScrollDriven, clamp01, ramp } from '../../hooks/useScrollDriven';

declare global {
  interface Window {
    /** Az index.html statikus hero-héjának első kifestett képkockája (performance.now()). */
    __heroShellAt?: number;
  }
}

const withSources = (img: { base: string; alt: string }) => ({
  ...img,
  src: imageSrc(img.base),
  srcSet: heroSrcSet(img.base),
});

// A diavetítés további képei; az első dia mindig a szezon képe (src/content/seasons.ts),
// ugyanaz, mint az index.html statikus héjában és a preloadban.
const DEFAULT_SLIDES = [
  { base: 'ahaaziko-0521-122149', alt: 'Haaziko terasz — panorámás kilátás a Pilis hegyeire' },
  { base: 'ahaaziko-0521-174926', alt: 'Haaziko alpesi faház a zöld domboldalon, délutáni fényben' },
  { base: 'ahaaziko-0521-142430', alt: 'Haaziko kert — természetközeli csend' },
  { base: 'ahaaziko-0521-213549', alt: 'Haaziko éjszaka — csillagos ég és tábortűz' },
];

/**
 * Parallax a hero-szekción belül (framer nélkül): p = a szekció kigörgetett aránya (0 = teteje a
 * viewport tetején, 1 = az alja a viewport tetején). Kép: y 0→150px, scale 1→1.15; szöveg: opacity 1→0
 * és y 0→-60px az első 40%-on; overlay 0.35→0.55.
 */
const heroVars = (scrollY: number, _vh: number, el: HTMLElement) => {
  const p = clamp01(scrollY / Math.max(1, el.offsetHeight));
  const t = ramp(p, 0, 0.4);
  return {
    '--hero-img-y': `${(p * 150).toFixed(1)}px`,
    '--hero-img-scale': (1 + p * 0.15).toFixed(4),
    '--hero-text-o': (1 - t).toFixed(3),
    '--hero-text-y': `${(-60 * t).toFixed(1)}px`,
    '--hero-overlay': (0.35 + p * 0.2).toFixed(3),
  };
};

export const Hero = () => {
  const t = useT();
  const { lang } = useLanguage();
  const season = seasonContent(lang);
  const heroImages = [
    withSources({ base: season.heroImage, alt: season.heroAlt }),
    ...DEFAULT_SLIDES.filter((s) => s.base !== season.heroImage).map(withSources),
  ];

  // Diavetítés: az éppen látható + az előző dia marad a DOM-ban (a friss felül, CSS-áttűnéssel).
  const [shown, setShown] = useState<number[]>([0]);
  useEffect(() => {
    const interval = setInterval(() => {
      setShown((prev) => {
        const next = (prev[prev.length - 1] + 1) % heroImages.length;
        return [...prev.slice(-1), next];
      });
    }, 6000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroImages.length]);

  const sectionRef = useScrollDriven<HTMLElement>(useCallback(heroVars, []));

  // Az index.html-ben lévő statikus hero-héj már elindította ugyanezeket a CSS-animációkat.
  // Mountkor annyival hátrébb (negatív delay) indítjuk őket, amennyi idő a héj kifestése óta eltelt,
  // így a szöveg onnan folytatja az animációt, ahol a héj tartott — nem villan, nem játszik le kétszer.
  const [shellElapsed] = useState(() =>
    typeof window !== 'undefined' && typeof window.__heroShellAt === 'number'
      ? (performance.now() - window.__heroShellAt) / 1000
      : 0,
  );
  const entrance = (delaySec: number) => ({ animationDelay: `${(delaySec - shellElapsed).toFixed(3)}s` });

  // Pre-launch ("Hamarosan") nézet: csak a hero kép + középre szedett egy szó.
  if (PRE_LAUNCH_MODE) {
    return (
      <section className="relative min-h-dvh overflow-hidden">
        <img
          src={heroImages[0].src}
          srcSet={heroImages[0].srcSet}
          sizes="100vw"
          alt={heroImages[0].alt}
          fetchPriority="high"
          width="1920"
          height="1080"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-forest/50" />
        <div className="relative z-10 min-h-dvh flex items-center justify-center px-6">
          <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl text-sand tracking-tight text-center hero-fade-in" style={{ animationDelay: '0.2s' }}>
            Hamarosan
          </h1>
        </div>
      </section>
    );
  }

  return (
    <section ref={sectionRef} className="relative min-h-dvh overflow-hidden">
      <div
        className="absolute inset-0 will-change-transform"
        style={{ transform: 'translateY(var(--hero-img-y, 0px)) scale(var(--hero-img-scale, 1))' }}
      >
        {shown.map((idx, i) => (
          <img
            key={idx}
            src={heroImages[idx].src}
            srcSet={heroImages[idx].srcSet}
            sizes="100vw"
            alt={heroImages[idx].alt}
            fetchPriority={idx === 0 ? 'high' : 'auto'}
            decoding="async"
            width="1920"
            height="1080"
            className={`absolute inset-0 w-full h-full object-cover ${i > 0 || idx !== 0 ? 'hero-xfade' : ''}`}
          />
        ))}
      </div>

      <div className="absolute inset-0 bg-forest" style={{ opacity: 'var(--hero-overlay, 0.35)' }} />

      <div className="relative z-10 min-h-dvh flex items-center justify-center">
        <div
          className="text-center px-6 flex flex-col items-center"
          style={{ opacity: 'var(--hero-text-o, 1)', transform: 'translateY(var(--hero-text-y, 0px))' }}
        >
          <span className="font-hero-sans text-sand/70 uppercase tracking-[0.4em] text-xs md:text-sm mb-6 hero-fade-in" style={entrance(0.1)}>
            {t.hero.tagline}
          </span>

          <h1 className="font-hero-serif text-7xl md:text-9xl lg:text-[11rem] tracking-tighter mb-6 leading-none text-sand">
            {'haaziko.'.split('').map((char, i) => (
              <span
                key={i}
                className="inline-block hero-char-in"
                style={entrance(0.25 + i * 0.065)}
              >
                {char}
              </span>
            ))}
          </h1>

          <p className="font-hero-sans text-sand/80 text-lg md:text-xl max-w-lg mx-auto font-light mb-16 hero-fade-in" style={entrance(0.3)}>
            {season.heroSubtitle}
          </p>

          <a
            href="#about"
            className="font-hero-sans inline-flex items-center text-sand/60 uppercase tracking-widest text-xs hover:text-sand transition-colors group hero-fade-in"
            style={entrance(0.4)}
          >
            {t.hero.cta}
            <span className="ml-3 w-8 h-px bg-sand/40 group-hover:w-16 group-hover:bg-sand transition-all duration-500 ease-out" />
          </a>
        </div>
      </div>

      <div
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3"
        style={{ opacity: 'var(--hero-text-o, 1)' }}
      >
        <div className="w-px h-12 bg-linear-to-b from-sand/50 to-transparent hero-bob" />
      </div>
    </section>
  );
};
